import React from 'react'

import { Button, Modal, ModalInjectedProps } from '@sketch/components'
import {
  GetProjectsListQuery,
  GetProjectsListQueryVariables,
  GetSharesQuery,
  ProjectFragment,
  ProjectListItemFragment,
  useArchiveProjectMutation,
} from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'
import {
  removeFromPaginated,
  removeFromStorageItemsList,
  useFolderTerm,
} from '@sketch/modules-common'
import { useEventDispatch } from '@sketch/utils'
import { evictFieldIf } from '@sketch/graphql-cache'

interface ArchiveProjectModalProps extends ModalInjectedProps {
  project: ProjectFragment | ProjectListItemFragment
  workspaceIdentifier: string
}

const ArchiveProjectModal = (props: ArchiveProjectModalProps) => {
  const { hideModal, project, workspaceIdentifier } = props
  const { showToast } = useToast()

  const { identifier: projectIdentifier, name } = project

  const dispatchSharesRefresh = useEventDispatch('workspaceShareRefresh')

  const [archiveProject, { loading }] = useArchiveProjectMutation({
    variables: { projectIdentifier },
    onError: 'show-toast',
    onCompleted: ({ archiveProject }) => {
      showToast(`“${archiveProject.project.name}” Archived`)
      hideModal()
    },
    update: cache => {
      const target = {
        __typename: 'Project',
        identifier: projectIdentifier,
      } satisfies Pick<ProjectFragment, 'identifier' | '__typename'>

      // Remove the project from the sidebar Query
      removeFromPaginated(cache, target, key => key.includes('.projects'))

      removeFromStorageItemsList({ cache, target })

      // Remove from cache every query related with this workspace shares
      // This way the view will be refetched from scratch if needed to make sure
      // we always get the correct server information
      const workspaceCacheId = cache.identify({
        __typename: 'Workspace',
        identifier: workspaceIdentifier,
      })
      const sharesFieldName: keyof GetSharesQuery['workspace'] = 'shares'
      cache.evict({
        id: workspaceCacheId,
        fieldName: sharesFieldName,
      })

      const projectFieldName: keyof GetProjectsListQuery['workspace'] =
        'projects'
      evictFieldIf<{ archived: GetProjectsListQueryVariables['archived'] }>(
        cache,
        {
          id: workspaceCacheId,
          fieldName: projectFieldName,
        },
        fieldArguments => fieldArguments?.archived === 'ARCHIVED'
      )

      cache.gc()

      // Force any visible query to refresh
      dispatchSharesRefresh({
        workspaceIdentifier,
        projectIdentifier,
      })
    },
  })

  const folderTerm = useFolderTerm()

  return (
    <Modal onCancel={hideModal}>
      <Modal.Header>Archive {folderTerm.upper}?</Modal.Header>
      <Modal.Body>
        The <strong>“{name}”</strong> {folderTerm.lower} and all its documents
        will be sent to the Archive. You’ll still be able to view and edit
        archived documents as usual.
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={hideModal}>Cancel</Button>
        <Button
          variant="primary"
          onClick={() => archiveProject()}
          loading={loading}
        >
          Archive {folderTerm.upper}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ArchiveProjectModal
